import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Clock, Phone, ShieldCheck, Star } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "./Nav";
import { scrollToSection } from "@/App";

const lines = [
  { text: "COMFORT", accent: false },
  { text: "YOU CAN", accent: false },
  { text: "COUNT ON.", accent: true },
];

const RevealLine = ({ text, accent, index }) => (
  <div className="overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{
        duration: 1,
        delay: 0.4 + index * 0.15,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={`block font-display font-bold uppercase tracking-tighter leading-[0.9] text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] ${
        accent ? "text-[#FF6B00]" : "text-white"
      }`}
    >
      {text}
    </motion.span>
  </div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen flex flex-col justify-end overflow-hidden noise-overlay"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1585314062340-f1a5a7c9328d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtZXRhbCUyMHRleHR1cmUlMjBkYXJrfGVufDB8fHx8MTc4NzkyMjg3N3ww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-[120%] object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/40" />
      </motion.div>

      <div
        className="absolute top-24 right-6 md:right-10 z-10 font-mono-tech text-[10px] md:text-xs uppercase tracking-[0.3em] text-zinc-500 [writing-mode:vertical-rl]"
        data-testid="hero-location-tag"
      >
        Knoxville — Maryville — East TN
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pb-16 md:pb-24 pt-40"
      >
        <div className="overflow-hidden mb-8">
          <motion.div
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-3 border border-[#FF6B00]/40 px-4 py-2 rounded-sm"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 bg-[#FF6B00] rounded-full animate-pulse" />
            <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#FF6B00]">
              Licensed &amp; Insured — Open 24/7
            </span>
          </motion.div>
        </div>

        <h1 className="mb-10" data-testid="hero-headline">
          {lines.map((line, i) => (
            <RevealLine key={line.text} text={line.text} accent={line.accent} index={i} />
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-xl text-base md:text-lg text-zinc-400 mb-12"
          data-testid="hero-subcopy"
        >
          Proudly serving East TN. Heating, cooling, and air quality done right —
          with 24/7 emergency service across Knoxville, Maryville, and beyond.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row items-start gap-4 mb-16"
        >
          <a
            href={PHONE_TEL}
            className="animate-pulse-ring group flex items-center gap-3 bg-[#FF6B00] hover:bg-[#E66000] text-[#050505] font-display font-semibold uppercase tracking-wide text-lg px-8 py-5 rounded-sm transition-colors duration-300"
            data-testid="hero-emergency-call-button"
          >
            <Phone className="w-5 h-5" strokeWidth={2.5} />
            24/7 Emergency — {PHONE_DISPLAY}
          </a>
          <button
            onClick={() => scrollToSection("quote")}
            className="flex items-center gap-3 border border-white/20 hover:border-[#FF6B00] hover:text-[#FF6B00] text-white font-display font-semibold uppercase tracking-wide text-lg px-8 py-5 rounded-sm transition-colors duration-300"
            data-testid="hero-estimate-button"
          >
            Free Estimate
            <ArrowDown className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="flex flex-wrap gap-x-10 gap-y-4 border-t border-white/10 pt-8"
          data-testid="hero-trust-bar"
        >
          <div className="flex items-center gap-3" data-testid="trust-rating">
            <Star className="w-5 h-5 text-[#FF6B00] fill-[#FF6B00]" />
            <div>
              <p className="font-display font-semibold text-lg leading-none">4.8 / 5</p>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                20+ Google Reviews
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-testid="trust-hours">
            <Clock className="w-5 h-5 text-[#FF6B00]" />
            <div>
              <p className="font-display font-semibold text-lg leading-none">24 Hours</p>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                7 Days A Week
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-testid="trust-licensed">
            <ShieldCheck className="w-5 h-5 text-[#FF6B00]" />
            <div>
              <p className="font-display font-semibold text-lg leading-none">Licensed</p>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                &amp; Fully Insured
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
